// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import * as L from 'leaflet';
import G from 'google-map-react';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import MapModal from 'wdna-components/lib/modals/generics/MapModal';
import { messageError, messageWarning, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { company, location, CompanyDO, LocationDO } from '../../../../management/requests';
import { typeSensor, TypeSensorListDO, command, CommandDO, lov, LovDO } from '../../../../configuration/requests';
import { sensor, firmware, waste, SensorDO, WasteContainerDO, FirmwareDO } from '../../../requests';

import Form from './Form';
import WasteForm from '../waste/Form';
import WasteEdit from '../waste/Edit';
import ButtonsForm from '../ButtonsForm';

const WASTE_TYPES: number[] = [11, 13];
const MAX_VAL: number = 3600 * 24;

const MIN_UNLOADS: Map<string, number> = new Map<string, number>([
  ['FOR', 10],
  ['GLA', 10],
  ['PAP', 7],
  ['PET', 7],
  ['PNG', 10],
  ['CRK', 10],
  ['SUW', 10],
]);

const MAX_WEIGHT: Map<string, number> = new Map<string, number>([
  ['120', 60],
  ['240', 100],
  ['800', 320],
  ['1000', 400],
]);

interface Props {
  rowId?: number;
  notify: () => void;
  duplicate: boolean;
  sensorList: any[];
}

interface State {
  loading: boolean;
  isNew: boolean;
  showMap: boolean;

  typeSensorArray: TypeSensorListDO[];
  companiesArray?: CompanyDO[];
  firmwareArray?: FirmwareDO[];
  locationArray?: LocationDO[];
  statusArray?: LovDO[];

  modelWasteArray?: LovDO[];
  typeWasteArray?: LovDO[];

  commandsArray: CommandDO[];
  suggestions: any[];

  timetable: {
    min: number;
    max: number;
  };

  data: {
    id?: number;
    code?: string;
    name?: string;
    persistent1: boolean;
    persistent2: boolean;
    update: boolean;
    dateLast: Date;
    command1: string;
    command2: string;
    timeNc: number;
    timetableNcList: number[][];
    latitude?: number;
    longitude?: number;
    typeSensor?: any;
  };

  dtype: {
    id?: number;
    date: Date;
    type?: string;
    model?: string;
    size?: string;
    minUnload: string;
    maxWeight: string;
    minTemp: number;
    maxTemp: number;
  };

  dinfo: {
    containerId?: number;
    date: Date;
    ratio1: number;
    ratio2: number;
    ratio3: number;
    ratio4: number;
  };
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private tabId: string;
  private mounted: boolean;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.tabId = window.getStr();
    this.mounted = false;
    this.defaultValues = {};

    this.state = {
      loading: false,
      isNew: true,
      showMap: false,

      typeSensorArray: [],
      companiesArray: [],
      firmwareArray: [],
      locationArray: [],
      statusArray: [],

      modelWasteArray: [],
      typeWasteArray: [],

      commandsArray: [],
      suggestions: [],

      timetable: { min: 0, max: 0 },

      data: {
        persistent1: false,
        persistent2: false,
        update: false,
        dateLast: new Date(),
        command1: '',
        command2: '',
        timeNc: 60,
        timetableNcList: [
          [0, MAX_VAL],
        ],
      },

      dtype: {
        date: new Date(),
        minUnload: '0',
        maxWeight: '0',
        minTemp: 1,
        maxTemp: 50,
      },

      dinfo: {
        date: new Date(),
        ratio1: 0,
        ratio2: 0,
        ratio3: 0,
        ratio4: 0,
      },
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    const { rowId } = this.props || {};
    const isNew: boolean = (rowId === undefined || !!isNaN(rowId));

    this.loadingStart();
    try {
      const wasteRow: any = this.state.dtype;
      const [typeSensorArray, firmwareArray, companiesArray, commandsArray, locationArray, statusArray, modelWasteArray, typeWasteArray] = await Promise.all([
        typeSensor<TypeSensorListDO[]>('list', undefined, { active: true }, { params: { order: 'typeModule' } }) || [],
        firmware<FirmwareDO[]>('list'),
        company<CompanyDO[]>('list'),
        command<CommandDO[]>('list') || [],
        location<LocationDO[]>('list'),
        lov<LovDO[]>('statics', 'sensor_status'),
        lov<LovDO[]>('statics', 'model_container'),
        lov<LovDO[]>('statics', 'type_waste'),
      ]);
      let rowData: any = this.state.data;
      if (!isNew) {
        rowData = await sensor('get', String(rowId));
      }
      if (this.mounted) {
        const { timetable } = this.state;
        if (rowData.timetableNcList) {
          timetable.min = rowData.timetableNcList[0][0] / 3600;
          timetable.max = rowData.timetableNcList[0][1] / 3600;
        }

        this.setState(
          {
            data: { ...rowData, },
            dtype: { ...wasteRow, },
            typeSensorArray,
            firmwareArray,
            companiesArray,
            locationArray,
            commandsArray,
            statusArray,
            modelWasteArray,
            typeWasteArray,
            timetable,
            isNew,
          },
          () => {
            this.defaultValues = _.cloneDeep(rowData);
          }
        );
        const { duplicate } = this.props;
        if (duplicate) {
          this.state.data.code = undefined;
          this.state.data.name = undefined;
        }
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadType = (): void => {
    const { rowId } = this.props || {};

    if (WASTE_TYPES.includes(parseInt(this.state.data.typeSensor.id, 10))) {
      const tabId: string = 'WE';
      const title: string = rowId ? `Waste-Container Edit: ${rowId}` : 'Waste-Container New';

      window.store.dispatch(removeTab(tabId));
      const tabData: TabConfig = {
        id: tabId,
        active: true,
        title,
        component: WasteEdit,
        props: {
          rowId,
          tabId: this.tabId,
          report: 'WASTE CONTAINERS',
          title: 'WasteContainers',
        },
      };
      window.store.dispatch(addTab(tabData));
    } else {
      messageWarning('Warning', 'There are no additional edition for this sensor type');
    }
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setState({
      data: {
        ...this.defaultValues,
      }
    });
  };

  public submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const { duplicate } = this.props;
    const { isNew, data, dtype } = this.state;
    if (duplicate) data.id = undefined;
    const isWaste: boolean = (data && data.typeSensor && (WASTE_TYPES.includes(parseInt(data.typeSensor.id, 10))));

    data.dateLast = new Date();
    data.timetableNcList = this.formatTimetable();
    if (!this.isValidForm(data)) return;
    if (isNew && isWaste) {
      if (!this.isValidForm2(dtype)) return;
    }

    this.loadingStart();
    try {
      const saved: SensorDO | undefined = await sensor<SensorDO>('set', undefined, data);

      // Sensor type save data
      if ((isNew && saved?.id) || (duplicate && saved?.id)) {
        // Waste Type
        if (WASTE_TYPES.includes(saved.typeSensor.id)) {
          const { dinfo } = this.state;
          dtype.id = saved.id;
          dinfo.containerId = saved.id;
          dinfo.date = dtype.date;
          await waste('set', undefined, dtype);
          await waste('info', 'set', dinfo);
        }
      }
      messageInfo('Success', `Sensor successfully ${data.id ? 'edited' : 'created'} ${saved?.name}`, 5000);
      const { notify } = this.props || {};
      if (typeof notify === 'function') {
        notify();
      }
      window.store.dispatch(removeTab('SE'));
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  }

  public isValidForm = (data: any): boolean => {
    // eslint-disable-next-line no-shadow
    const { status, typeSensor, code, update } = data;
    const { rowId } = this.props || '';
    const { sensorList } = this.props || [];
    if (code.length !== 7) {
      messageError('Error', 'Incorrect sensor code, must have 7 digits');
      return false;
    }
    if (!status) {
      messageError('Error', 'Please select the status of the sensor');
      return false;
    }
    if (!typeSensor) {
      messageError('Error', 'Please select a type of sensor');
      return false;
    }
    if (!data.company) {
      messageError('Error', 'Please select a company of sensor');
      return false;
    }
    // Check only for new entries
    if (rowId === undefined || isNaN(rowId)) {
      // Non repeated codes
      const usedCodes: Set<number> = sensorList.reduce((result: Set<number>, item: any) => {
        if (item.typeSensorId === +typeSensor.id) result.add(item.code);
        return result;
      }, new Set([]));
      const isDuplicateCode: boolean = usedCodes.has(code);
      if (isDuplicateCode) {
        messageWarning('Warning', 'The device code you entered is already in use');
        return false;
      }
    }
    // Selected firmware if update
    if (update && (!data.firmware || !data.firmware.id)) {
      messageWarning('Warning', 'You must select a firmware to update sensor');
      return false;
    }

    return true;
  }

  public isValidForm2 = (dtype: any): boolean => {
    const { data } = this.state;
    const { type, model } = dtype;
    const isNew: boolean = (data.id === undefined || !!isNaN(data.id));
    const isWaste: boolean = (data && data.typeSensor && (WASTE_TYPES.includes(data.typeSensor.id)));

    // isNew and isWaste:
    if (isNew && isWaste) {
      if (!type) {
        messageError('Error', 'Please select the type of the container');
        return false;
      }
      if (!model) {
        messageError('Error', 'Please select the model of the container');
        return false;
      }
    }

    return true;
  }

  public formatTimetable = (): number[][] => {
    const { timetable } = this.state;
    const timetableList: number[][] = [
      [(timetable.min * 3600), (timetable.max * 3600)],
    ];
    return timetableList;
  }

  public handleChangeRange = (name: string, value: any): void => {
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }));
  }

  public handleChangeStandard = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        [name]: value,
      },
    }));
  }

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset, tagName } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { handlerType } = dataset || {};
    if (tagName === 'SELECT') {
      const [outerName, innerName] = name.split('.');
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [outerName]: {
            [innerName]: value,
          },
        },
      }));
    } else if (type === 'checkbox') {
      if (handlerType === 'integer') {
        this.setState((prevState: State) => ({
          data: {
            ...prevState.data,
            [name]: checked ? 1 : 0,
          },
        }));
      } else {
        this.setState((prevState: State) => ({
          data: {
            ...prevState.data,
            [name]: !!checked,
          },
        }));
      }
    }
  };

  public handleChangeType = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | Date | null): void => {
    if (e instanceof Date) {
      this.setState((prevState: State) => ({
        dtype: {
          ...prevState.dtype,
          date: e,
        },
      }));
    } else {
      const { name, value, checked, type, dataset, tagName } = (e as React.ChangeEvent<HTMLInputElement>).target;
      const { handlerType } = dataset || {};
      if (name === 'type') {
        let min: string = '0';
        if ((value === 'SUW' || value === 'FOR' || value === 'GLA' || value === 'PNG' || value === 'CRK')) {
          min = '10';
        }
        if (value === 'PAP' || value === 'PET') {
          min = '7';
        }
        this.setState((prevState: State) => ({
          dtype: {
            ...prevState.dtype,
            minUnload: min,
          },
        }));
      }
      if (name === 'size') {
        let max: number = 0;
        if (value === '120') max = 60;
        if (value === '240') max = 100;
        if (value === '800') max = 320;
        if (value === '1000') max = 400;
        this.setState((prevState: State) => ({
          dtype: {
            ...prevState.dtype,
            maxWeight: max.toString(),
          },
        }));
      }
      if (tagName === 'SELECT') {
        const [outerName, innerName] = name.split('.');
        this.setState((prevState: State) => ({
          dtype: {
            ...prevState.dtype,
            [outerName]: {
              [innerName]: value,
            },
          },
        }));
      }
      if (type === 'checkbox') {
        if (handlerType === 'integer') {
          this.setState((prevState: State) => ({
            dtype: {
              ...prevState.dtype,
              [name]: checked ? 1 : 0,
            },
          }));
        } else {
          this.setState((prevState: State) => ({
            dtype: {
              ...prevState.dtype,
              [name]: !!checked,
            },
          }));
        }
      } else {
        this.setState((prevState: State) => ({
          dtype: {
            ...prevState.dtype,
            [name]: value,
          },
        }));
      }
    }
  };

  public handleChangeInfo = (e: React.ChangeEvent<HTMLInputElement> | Date | null): void => {
    if (e instanceof Date) {
      this.setState((prevState: State) => ({
        dinfo: {
          ...prevState.dinfo,
          date: e,
        },
      }));
    } else {
      const { name, value } = (e as React.ChangeEvent<HTMLInputElement>).target;
      this.setState((prevState: State) => ({
        dinfo: {
          ...prevState.dinfo,
          [name]: value,
        },
      }));
    }
  };

  public handleChangeUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.handleChange(e);
    const { checked } = e.target;
    if (!checked) {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          firmware: {},
        },
      }));
    }
  }

  public handleChangeLocation = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;
    const [outerName, innerName] = name.split('.');

    const { data, locationArray } = this.state;
    let { latitude, longitude } = data;
    if (!_.isEmpty(value) && (!_.isNumber(latitude) || !_.isNumber(longitude))) {
      const loc: LocationDO | undefined = locationArray?.find((item: LocationDO) => item.id === Number(value));
      latitude = loc?.latitude;
      longitude = loc?.longitude;
    }

    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        [outerName]: {
          [innerName]: value,
        },
        latitude,
        longitude
      }
    }));
  }

  public getSuggestions = (value: string): any[] => {
    const inputValue: string = value.trim().toLowerCase();
    const inputLength: number = inputValue.length;

    return inputLength === 0
      ? []
      : this.state.commandsArray.filter((comm: any) => comm.value.toLowerCase().slice(0, inputLength) === inputValue);
  }

  public getSuggestionValue = (suggestion: any): string => {
    return suggestion.value;
  }

  public renderSuggestion = (suggestion: any): JSX.Element => {
    return <div>{suggestion.value}</div>;
  }

  public handleChangeSuggestions1 = (e: any, { newValue }: any): void => {
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        command1: newValue,
      },
    }));
  };

  public handleChangeSuggestions2 = (e: any, { newValue }: any): void => {
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        command2: newValue,
      },
    }));
  }

  public handleSuggestionsFetchRequested = ({ value }: any): void => {
    this.setState({
      suggestions: this.getSuggestions(value),
    });
  }

  public handleSuggestionsClearRequested = (): void => {
    this.setState({
      suggestions: [],
    });
  }

  public handleMapClick = (e: L.LeafletMouseEvent | G.ClickEventValue): void => {
    const lat: number = (e as G.ClickEventValue).lat || (e as L.LeafletMouseEvent).latlng.lat;
    const lng: number = (e as G.ClickEventValue).lng || (e as L.LeafletMouseEvent).latlng.lng;
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        latitude: lat,
        longitude: lng,
      },
    }));
  }

  public showMap = (): void => {
    this.setState({
      showMap: true,
    });
  }

  public closeMap = (): void => {
    this.setState({
      showMap: false,
    });
  }

  // TODO: duplicated code ??
  public autoFormMin = (data: WasteContainerDO): number => {
    const { type } = data;
    return MIN_UNLOADS.get(type) || 0;
  }

  public autoFormMax = (data: WasteContainerDO): number => {
    const { size } = data;
    return MAX_WEIGHT.get(String(size)) || 0;
  }

  public render(): JSX.Element {
    const { suggestions, data } = this.state;
    const { duplicate } = this.props;

    const inputProps1: any = {
      placeholder: 'Type a command',
      value: data.command1 || '',
      style: { width: '550px' },
      onChange: this.handleChangeSuggestions1,
    };

    const inputProps2: any = {
      placeholder: 'Type a command',
      value: data.command2 || '',
      style: { width: '550px' },
      onChange: this.handleChangeSuggestions2,
    };

    const isNew: boolean = (data.id === undefined || !!isNaN(data.id));
    const isWaste: boolean = (data && data.typeSensor && (WASTE_TYPES.includes(parseInt(data.typeSensor.id, 10))));

    let mapZoom: number = 6;
    let mapInitialMapCenter: [number, number] = [40.6, 1.13];
    if (_.isNumber(data.latitude) && _.isNumber(data.longitude)) {
      mapZoom = 10;
      mapInitialMapCenter = [data.latitude, data.longitude];
    }

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={this.state.loading} overlay={false} />
                  <UiValidate>
                    <form
                      className="smart-form"
                      onSubmit={(e: React.FormEvent<HTMLFormElement>): void => { this.submit(e).catch(() => { /* pass */ }); }}
                      method="post"
                      encType="multipart/form-data"
                    >
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Sensor {(data.id && !this.props.duplicate) ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form
                        formValues={this.state}
                        onChange={this.handleChange}
                        onChangeRange={this.handleChangeRange}
                        onChangeStandard={this.handleChangeStandard}
                        onChangeUpdate={this.handleChangeUpdate}
                        onChangeLocation={this.handleChangeLocation}
                        suggestions={suggestions}
                        onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
                        onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
                        getSuggestionValue={this.getSuggestionValue}
                        renderSuggestion={this.renderSuggestion}
                        inputProps1={inputProps1}
                        inputProps2={inputProps2}
                        showMap={this.showMap}
                        loadType={this.loadType}
                        duplicate={duplicate}
                      />
                      {((isNew && isWaste) || (duplicate && isWaste)) && (
                        <div>
                          <header>
                            <h2>
                              <span className="widget-icon" style={{ padding: '8px' }}>
                                <i className="fa fa-edit" />
                              </span>
                              Waste Container Info
                            </h2>
                          </header>
                          <WasteForm
                            onChange={this.handleChangeType}
                            onChangeInfo={this.handleChangeInfo}
                            formValues={this.state as any}
                            duplicate={duplicate}
                            onAutoFormMin={this.autoFormMin}
                            onAutoFormMax={this.autoFormMax}
                          />
                        </div>
                      )}
                      <MapModal
                        zoom={mapZoom}
                        initialMapCenter={mapInitialMapCenter}
                        latitude={data.latitude || 0}
                        longitude={data.longitude || 0}
                        onMapClick={this.handleMapClick}
                        title="Select coordinates of the sensor"
                        show={this.state.showMap}
                        onCancel={this.closeMap}
                        useGoogleMaps={false}
                      />
                      <ButtonsForm formValues={this.state} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
